import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { MapPin, Calendar, Users, TrendingUp, ArrowLeft, Heart, MessageCircle, ChevronDown, ChevronUp, Receipt, Trash2, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import WorkProgressDisplay from './WorkProgressDisplay';
import WorkExecutionManager from './WorkExecutionManager';
import TermsModal from './TermsModal';

const ReportDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [report, setReport] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showExpenses, setShowExpenses] = useState(false);
  const [showSupporters, setShowSupporters] = useState(false);

  useEffect(() => {
    fetchReport();
    fetchDonations();
    fetchComments();
  }, [id]);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/reports/${id}`);
      setReport(res.data);
    } catch (err) {
      toast.error(t('msg.error'));
      navigate('/reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchDonations = async () => {
    try {
      const res = await axios.get(`${API}/donations/report/${id}`);
      setDonations(res.data);
    } catch (err) {
      console.error('Failed to fetch donations');
    }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/reports/${id}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error('Failed to fetch comments');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to comment');
      navigate('/');
      return;
    }

    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    try {
      await axios.post(`${API}/reports/${id}/comments`, {
        comment: newComment
      });
      setNewComment('');
      fetchComments();
      toast.success(t('msg.commentAdded'));
    } catch (err) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/reports/${id}/comments/${commentId}`);
      fetchComments();
      toast.success(t('msg.commentDeleted'));
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  const getRefundEligibility = () => {
    if (!report?.estimated_cost) return null;
    
    const fundingPct = (report.collected_amount / report.estimated_cost) * 100;
    
    if (fundingPct >= 80) {
      return {
        allowed: false,
        message: '⚠️ Funding reached 80%. Refunds require moderator approval.',
        color: 'orange'
      };
    }
    
    return {
      allowed: true,
      message: '✓ Direct refunds available',
      color: 'green'
    };
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleDonate = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to donate');
      navigate('/');
      return;
    }

    const amount = parseInt(donationAmount);
    if (amount < 10) {
      toast.error('Minimum donation is ₹10');
      return;
    }

    // Check terms acceptance
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/donations/order`, {
        report_id: id,
        amount,
        terms_accepted: termsAccepted
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const razorpayLoaded = await loadRazorpay();
      if (!razorpayLoaded) {
        toast.error('Failed to load payment gateway');
        setProcessing(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: res.data.amount,
        currency: res.data.currency,
        order_id: res.data.order_id,
        name: 'FixOurIndia',
        description: report.title,
        handler: async (response) => {
          try {
            const formData = new FormData();
            formData.append('razorpay_payment_id', response.razorpay_payment_id);
            formData.append('razorpay_order_id', response.razorpay_order_id);
            formData.append('razorpay_signature', response.razorpay_signature);

            await axios.post(`${API}/donations/verify`, formData);
            
            toast.success(t('msg.donationSuccess'));
            setShowDonateModal(false);
            setDonationAmount('');
            fetchReport();
            fetchDonations();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name || '',
          contact: user.phone || ''
        },
        theme: {
          color: '#0891b2'
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Donation failed');
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getProgressPercentage = () => {
    if (!report?.estimated_cost) return 0;
    return Math.min((report.collected_amount / report.estimated_cost) * 100, 100);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!report) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <button
            data-testid="back-to-reports-btn"
            onClick={() => navigate('/reports')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t("nav.backReports")}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Report Info */}
            <div className="card p-8">
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-3xl font-bold text-gray-900 flex-1" data-testid="report-title">{report.title}</h1>
                <span className={`status-badge status-${report.status}`} data-testid="report-status">
                  {report.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-6">
                <div className="flex items-center">
                  <MapPin size={16} className="mr-1" />
                  {report.location.address}
                  <a
                    href={`https://www.google.com/maps?q=${report.location.lat},${report.location.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 text-cyan-600 hover:text-cyan-700 font-medium underline"
                    data-testid="report-map-link"
                  >
                    Open in Google Maps
                  </a>
                </div>
                <div className="flex items-center">
                  <Calendar size={16} className="mr-1" />
                  {formatDate(report.created_at)}
                </div>
                <div className="px-3 py-1 bg-gray-100 rounded-full capitalize">
                  {report.category}
                </div>
                {report.urgency !== 'normal' && (
                  <div className="px-3 py-1 bg-red-100 text-red-700 rounded-full capitalize flex items-center">
                    <TrendingUp size={14} className="mr-1" />
                    {report.urgency} Priority
                  </div>
                )}
              </div>

              <p className="text-gray-700 leading-relaxed mb-6" data-testid="report-description">{report.description}</p>

              {/* Photos */}
              {report.photos.length > 0 && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {report.photos.map((photo, idx) => (
                    <img
                      key={idx}
                      src={photo}
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-64 object-cover rounded-lg"
                      data-testid={`report-photo-${idx}`}
                    />
                  ))}
                </div>
              )}

              {report.verification_comments && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Verification Comments</h3>
                  <p className="text-blue-800 text-sm">{report.verification_comments}</p>
                </div>
              )}
            </div>

            {/* Work Progress Display (Public View) */}
            <WorkProgressDisplay report={report} />

            {/* Moderator Work Execution Manager */}
            {user && user.role === 'moderator' && report.status !== 'submitted' && (
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h2 className="text-xl font-bold mb-4">Work Execution Management</h2>
                <WorkExecutionManager report={report} onUpdate={fetchReport} />
              </div>
            )}

            {/* Comments Section */}
            <div className="card p-8">
              <h2 className="text-xl font-bold mb-6 flex items-center">
                <MessageCircle size={24} className="mr-2 text-cyan-600" />
                {t("comments.title")} ({comments.length})
              </h2>

              {/* Add Comment Form */}
              {user && (
                <form onSubmit={handleAddComment} className="mb-6">
                  <Textarea
                    data-testid="comment-input"
                    placeholder={t("comments.placeholder")}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={3}
                    className="mb-2"
                  />
                  <Button data-testid="submit-comment-btn" type="submit">
                    {t("comments.post")}
                  </Button>
                </form>
              )}

              {!user && (
                <div className="mb-6 p-4 bg-blue-50 text-blue-800 rounded-lg text-sm">
                  {t("comments.loginRequired")}
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No comments yet. Be the first to comment!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="border-b border-gray-200 pb-4" data-testid={`comment-${comment.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="font-semibold text-gray-900">{comment.user_name}</span>
                          <span className="text-xs text-gray-500 ml-2">{formatDate(comment.created_at)}</span>
                        </div>
                        {user && user.id === comment.user_id && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-comment-${comment.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <p className="text-gray-700">{comment.comment}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Funding Card */}
            {report.estimated_cost ? (
              <div className="card p-6">
                <h3 className="font-semibold text-lg mb-4">Funding Progress</h3>
                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-2xl font-bold text-gray-900" data-testid="collected-amount">
                      ₹{report.collected_amount.toLocaleString()}
                    </span>
                    <span className="text-gray-500">of ₹{report.estimated_cost.toLocaleString()}</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${getProgressPercentage()}%` }}
                      data-testid="progress-bar"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {getProgressPercentage().toFixed(1)}% funded
                  </p>
                </div>

                <div className="space-y-2 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Supporters</span>
                    <span className="font-semibold" data-testid="supporters-count">{donations.length}</span>
                  </div>
                  {report.close_date && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Deadline</span>
                      <span className="font-semibold">{formatDate(report.close_date)}</span>
                    </div>
                  )}
                </div>

                {(report.status === 'verified' || report.status === 'fundraising' || report.status === 'in_progress') && (
                  <Button
                    data-testid="donate-btn"
                    onClick={() => setShowDonateModal(true)}
                    className="w-full btn-primary mb-4"
                  >
                    <Heart size={18} className="mr-2" />
                    Support This Cause
                  </Button>
                )}

                {/* Expenses Section */}
                {report.expenses && report.expenses.length > 0 && (
                  <Collapsible open={showExpenses} onOpenChange={setShowExpenses} className="mb-4">
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg" data-testid="expenses-toggle">
                      <span className="flex items-center font-medium text-gray-900">
                        <Receipt size={18} className="mr-2" />
                        Expenses Breakdown
                      </span>
                      {showExpenses ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-2">
                      {report.expenses.map((expense, idx) => (
                        <div key={idx} className="flex justify-between text-sm p-2 bg-gray-50 rounded" data-testid={`expense-${idx}`}>
                          <span className="text-gray-700">{expense.category}</span>
                          <span className="font-semibold text-gray-900">₹{expense.amount.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span>₹{report.expenses.reduce((sum, e) => sum + e.amount, 0).toLocaleString()}</span>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Supporters Section */}
                {donations.length > 0 && (
                  <Collapsible open={showSupporters} onOpenChange={setShowSupporters}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg" data-testid="supporters-toggle">
                      <span className="flex items-center font-medium text-gray-900">
                        <Users size={18} className="mr-2" />
                        Supporters ({donations.length})
                      </span>
                      {showSupporters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                      {donations.map((donation, idx) => (
                        <div
                          key={donation.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          data-testid={`donation-${idx}`}
                        >
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center text-xs font-semibold mr-2">
                              {idx + 1}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">Supporter {idx + 1}</div>
                              <div className="text-xs text-gray-500">{formatDate(donation.created_at)}</div>
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-cyan-600">₹{donation.amount.toLocaleString()}</div>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* Refund Policy */}
                {getRefundEligibility() && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${
                    getRefundEligibility().color === 'green' 
                      ? 'bg-green-50 text-green-800' 
                      : 'bg-orange-50 text-orange-800'
                  }`}>
                    <p className="font-medium mb-1">Refund Policy:</p>
                    <p>{getRefundEligibility().message}</p>
                    <p className="text-xs mt-2">
                      • Refunds available after 24 hours
                      {getRefundEligibility().allowed && (
                        <><br />• Auto-approved before 80% funding</>
                      )}
                      {!getRefundEligibility().allowed && (
                        <><br />• Requires moderator approval after 80%</>
                      )}
                    </p>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-4">
                  Platform adds 10% operational fee to the target amount.
                </p>
              </div>
            ) : (
              <div className="card p-6">
                <h3 className="font-semibold text-lg mb-2">Under Review</h3>
                <p className="text-sm text-gray-600">
                  This report is being verified by our team. Estimated cost will be available soon.
                </p>
              </div>
            )}

            {/* Support Ticket */}
            {user && (
              <div className="card p-6">
                <h3 className="font-semibold text-lg mb-2">Need Help?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Have questions or need to request a refund?
                </p>
                <Button
                  data-testid="create-support-ticket-btn"
                  onClick={() => navigate('/support')}
                  variant="outline"
                  className="w-full"
                >
                  Create Support Ticket
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Donate Modal */}
      <Dialog open={showDonateModal} onOpenChange={setShowDonateModal}>
        <DialogContent data-testid="donate-modal">
          <DialogHeader>
            <DialogTitle>Support This Cause</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDonate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Donation Amount (₹)</label>
              <Input
                data-testid="donation-amount-input"
                type="number"
                min="10"
                placeholder="Enter amount (min ₹10)"
                value={donationAmount}
                onChange={(e) => setDonationAmount(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Minimum donation: ₹10</p>
            </div>

            {/* Terms & Conditions Checkbox */}
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="terms-checkbox" className="text-sm">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-cyan-600 hover:underline"
                >
                  Terms & Conditions
                </button>{' '}
                and{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-cyan-600 hover:underline"
                >
                  Refund Policy
                </button>
              </label>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                Your contribution will help fix this issue. All transactions are processed securely through Razorpay.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="confirm-donate-btn"
                type="submit"
                className="flex-1"
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Donate Now'}
              </Button>
              <Button
                data-testid="cancel-donate-btn"
                type="button"
                variant="outline"
                onClick={() => setShowDonateModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Terms & Conditions Modal */}
      <TermsModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setShowTermsModal(false);
          toast.success('Terms accepted');
        }}
      />
    </div>
  );
};

export default ReportDetail;